import {
  getBinarySuccessThreshold,
  getMetricType,
  isRangeOperator,
  METRIC_TYPE,
  type ResolvedThreshold,
} from "@/metrics/metric-display"

import { PRIMARY_BAR_SERIES_COLOR } from "@/components/charts/seriesPalette"

import type {
  ChartClickData,
  ChartDataPoint,
  ChartWidgetConfig,
} from "../../../types/chart"
import {
  getPercentAxisTicks,
  getStackedSeriesMax,
} from "../../../utils/chart/axisTicks"
import { getColorForStringValue } from "../../../utils/chart/colorUtils"
import {
  parseSplitKey,
  UNGROUPED_SERIES_KEY,
  UNICODE_SEPARATOR,
} from "../../../utils/chart/splitKeys"
import { getOriginalSeriesIndex } from "../../../utils/chart/visibilityUtils"
import { resolveDataSource } from "../../../utils/dataSource"

export interface WholeRangeBarDatum {
  key: string
  name: string
  value: number
  fill: string
}

export interface GroupedBarKeys {
  groups: Record<string, string[]>
  groupLabels: string[]
  stringValueColorMap: Record<string, number>
}

interface GroupedBarDefinition {
  key: string
  groupId: string
  stringValue: string
  colorIndex: number
  showInLegend: boolean
}

interface BuildWholeRangeBarDataArgs {
  chartData: ChartDataPoint[]
  visibleSplitKeys: string[]
  splitKeys: string[]
  config: ChartWidgetConfig
  colors: readonly string[]
  targetValues: Parameters<typeof getColorForStringValue>[6]
  getSeriesDisplayName: (key: string) => string
}

export function getResponsiveScrollAreaHeight(
  containerHeight: number,
  minHeight: number,
  maxHeight: number,
  heightRatio: number,
): number {
  if (containerHeight <= 0) {
    return minHeight
  }
  return Math.min(
    maxHeight,
    Math.max(minHeight, Math.round(containerHeight * heightRatio)),
  )
}

export function getBarCellOpacity(
  hoveredBar: { dataIndex: number; barKey: string } | null,
  cellIndex: number,
  barKey: string,
): number {
  return hoveredBar?.dataIndex === cellIndex && hoveredBar.barKey === barKey
    ? 0.8
    : 1
}

export function truncateBarLabel(label: string, maxCharacters = 60): string {
  return label.length > maxCharacters
    ? `${label.substring(0, maxCharacters)}...`
    : label
}

export function getBarThresholdLines(
  metric: Parameters<typeof getMetricType>[0] | null | undefined,
  threshold: ResolvedThreshold | null,
  config: ChartWidgetConfig,
): number[] {
  if (
    !metric ||
    threshold?.target_float_upper == null ||
    config.showTargetZone === false ||
    config.aggregation === "count" ||
    config.aggregation === "sum"
  ) {
    return []
  }

  if (config.aggregation === "success") {
    return getMetricType(metric) === METRIC_TYPE.BINARY
      ? [getBinarySuccessThreshold(threshold)]
      : []
  }

  const upper = threshold.target_float_upper
  const lower = threshold.target_float_lower ?? upper
  return isRangeOperator(threshold.comparison_operator)
    ? [lower, upper]
    : [upper]
}

export function buildWholeRangeBarData({
  chartData,
  visibleSplitKeys,
  splitKeys,
  config,
  colors,
  targetValues,
  getSeriesDisplayName,
}: BuildWholeRangeBarDataArgs): WholeRangeBarDatum[] {
  if (chartData.length === 0) {
    return []
  }

  const totalRow = chartData[0]
  const keys =
    visibleSplitKeys.length > 0
      ? visibleSplitKeys
      : Object.keys(totalRow).filter(
          (key) => !["date", "timestamp", "bucketTime"].includes(key),
        )

  return keys
    .map((key) => {
      const rawValue = totalRow[key]
      const value = typeof rawValue === "number" ? rawValue : Number(rawValue)
      if (Number.isNaN(value)) {
        return null
      }

      const parsed = parseSplitKey(key)
      const originalIndex = getOriginalSeriesIndex(key, splitKeys)
      const isStringMetric = config.metricOutputType === "string"
      const isSplitBy = Boolean(
        config.groupBy || config.splitBy || config.groupByMetadataKey,
      )
      const fullKeyColor =
        isStringMetric && isSplitBy ? config.customColorMap?.[key] : undefined
      const fill =
        fullKeyColor ||
        (key === "value"
          ? PRIMARY_BAR_SERIES_COLOR
          : getColorForStringValue(
              parsed.stringValue,
              originalIndex,
              config,
              colors,
              undefined,
              undefined,
              targetValues,
            ))

      return {
        key,
        name: getSeriesDisplayName(key),
        value,
        fill,
      }
    })
    .filter((item): item is WholeRangeBarDatum => item !== null)
    .sort((left, right) => right.value - left.value)
}

export function buildGroupedBarKeys(
  config: ChartWidgetConfig,
  visibleSplitKeys: string[],
  getGroupDisplayName: (groupId: string) => string,
): GroupedBarKeys | null {
  if (!config.grouped || !config.stacked) {
    return null
  }

  const groups: Record<string, string[]> = {}
  const groupLabels: string[] = []
  const stringValues = new Set<string>()

  visibleSplitKeys.forEach((key) => {
    if (!key.includes(UNICODE_SEPARATOR)) {
      return
    }
    const { groupId, stringValue } = parseSplitKey(key)
    if (groupId === undefined) {
      return
    }
    if (!groups[groupId]) {
      groups[groupId] = []
      groupLabels.push(groupId)
    }
    groups[groupId].push(key)
    stringValues.add(stringValue)
  })

  if (Object.keys(groups).length === 0) {
    return null
  }

  const sortedGroupLabels = [...groupLabels].sort((left, right) =>
    getGroupDisplayName(left).localeCompare(getGroupDisplayName(right)),
  )
  const stringValueColorMap = Object.fromEntries(
    Array.from(stringValues)
      .sort()
      .map((value, index) => [value, index]),
  )

  return {
    groups,
    groupLabels: sortedGroupLabels,
    stringValueColorMap,
  }
}

export function buildGroupedBarDefinitions(
  groupedKeys: GroupedBarKeys | null,
): GroupedBarDefinition[] | null {
  if (!groupedKeys) {
    return null
  }

  const legendEntries = new Set<string>()
  return groupedKeys.groupLabels.flatMap((groupId) =>
    groupedKeys.groups[groupId].map((key) => {
      const { stringValue } = parseSplitKey(key)
      const showInLegend = !legendEntries.has(stringValue)
      legendEntries.add(stringValue)
      return {
        key,
        groupId,
        stringValue,
        colorIndex: groupedKeys.stringValueColorMap[stringValue] ?? 0,
        showInLegend,
      }
    }),
  )
}

export function resolveBarSelection(
  dataKey: string,
  config: ChartWidgetConfig,
) {
  const isStringMetric = config.metricOutputType === "string"
  const isSplitBy = Boolean(
    config.groupBy || config.splitBy || config.groupByMetadataKey,
  )
  let splitValue: string | undefined
  let stringValue: string | undefined

  if (isStringMetric && !isSplitBy && dataKey !== UNGROUPED_SERIES_KEY) {
    stringValue = dataKey
  } else if (isSplitBy) {
    splitValue = dataKey
    if (isStringMetric && dataKey.includes(UNICODE_SEPARATOR)) {
      const parsed = parseSplitKey(dataKey)
      splitValue = parsed.groupId
      stringValue = parsed.stringValue
    }
  }
  return { splitValue, stringValue }
}

export function createTimeSeriesBarClickData(
  data: unknown,
  dataKey: string,
  config: ChartWidgetConfig,
): ChartClickData | null {
  if (!data || typeof data !== "object") {
    return null
  }

  const dataRecord = data as Record<string, unknown>
  const clickedValue = dataRecord[dataKey]
  if (
    clickedValue === undefined ||
    clickedValue === null ||
    typeof dataRecord.timestamp !== "number" ||
    typeof dataRecord.bucketTime !== "string"
  ) {
    return null
  }

  const { splitValue, stringValue } = resolveBarSelection(dataKey, config)
  return {
    timestamp: dataRecord.timestamp,
    bucketTime: dataRecord.bucketTime,
    metricId: config.metricId || "",
    dataSource: resolveDataSource(config),
    aggregation: config.aggregation,
    metricOutputType: config.metricOutputType,
    bucketInterval: config.bucketInterval,
    splitBy: config.groupBy || config.splitBy,
    splitValue,
    stringValue,
    filters: config.filters,
    metricFilter: config.metricFilter,
    aggregatedValue:
      typeof clickedValue === "number" || typeof clickedValue === "string"
        ? clickedValue
        : 0,
    displayLabel: config.title || "Metric",
  }
}

export function createWholeRangeBarClickData(
  data: unknown,
  config: ChartWidgetConfig,
  dateRange?: { start: Date; end: Date } | null,
): ChartClickData | null {
  if (!data || typeof data !== "object") {
    return null
  }

  const dataRecord = data as WholeRangeBarDatum
  if (
    typeof dataRecord.key !== "string" ||
    typeof dataRecord.name !== "string" ||
    typeof dataRecord.value !== "number"
  ) {
    return null
  }

  const { splitValue, stringValue } = resolveBarSelection(
    dataRecord.key,
    config,
  )
  const rangeStart = dateRange?.start ?? new Date()
  const rangeEnd = dateRange?.end ?? rangeStart

  return {
    timestamp: rangeStart.getTime(),
    bucketTime: rangeStart.toISOString(),
    endDate: rangeEnd.toISOString(),
    metricId: config.metricId || "",
    dataSource: resolveDataSource(config),
    aggregation: config.aggregation,
    metricOutputType: config.metricOutputType,
    bucketInterval: config.bucketInterval,
    splitBy: config.groupBy || config.splitBy,
    splitValue,
    stringValue,
    filters: config.filters,
    metricFilter: config.metricFilter,
    aggregatedValue: dataRecord.value,
    displayLabel: dataRecord.name,
  }
}

/**
 * Success-rate y-axis ticks for a time-series bar chart, scaled to the
 * tallest drawn bar or stack and any threshold line. Undefined for other
 * aggregations, which keep Recharts' automatic domain.
 */
export function getBarPercentAxisTicks({
  chartData,
  config,
  groupedKeys,
  visibleSplitKeys,
  hasSplitKeys,
  thresholdLines,
}: {
  chartData: ChartDataPoint[]
  config: ChartWidgetConfig
  groupedKeys: GroupedBarKeys | null
  visibleSplitKeys: string[]
  hasSplitKeys: boolean
  thresholdLines: readonly number[]
}): number[] | undefined {
  if (config.aggregation !== "success") {
    return undefined
  }
  if (config.showAsPercentage && config.stacked) {
    return getPercentAxisTicks(1)
  }
  const stacks = !hasSplitKeys
    ? [["value"]]
    : groupedKeys
      ? groupedKeys.groupLabels.map((groupId) => groupedKeys.groups[groupId])
      : config.stacked
        ? [visibleSplitKeys]
        : visibleSplitKeys.map((key) => [key])
  return getPercentAxisTicks(
    getStackedSeriesMax(chartData, stacks),
    thresholdLines,
  )
}
